"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import type { DetailedMatchingResponse, MatchingResponse, PersonInput } from "@/lib/api/kundali";
import { reportOfferCopyFor } from "@/lib/report-offer-copy";
import { trackAgentRevenueOfferShown } from "@/lib/analytics";
import { useImpressionOnce } from "@/hooks/useImpressionOnce";
import SavedLinkCard from "@/components/SavedLinkCard";
import { ValueMomentContactCapture } from "@/components/ValueMomentContactCapture";
import { DeeperInsightsCTA } from "./DeeperInsightsCTA";

// The ONE source for the Gun Milan value surface. Rendered by the inline
// kundli-matching tool AND the /match/<id> share surface so the value moment
// and its intent-matched offer can never drift apart (rule 46: when a result
// relocates, the monetization travels with the exact same component tree).

// Post-submission result components — loaded only when a result renders
const MatchingScoreCard = dynamic(
  () => import("@/components/matching/MatchingScoreCard").then((m) => m.MatchingScoreCard),
  { ssr: false, loading: () => null }
);
const KootaBreakdown = dynamic(
  () => import("@/components/matching/KootaBreakdown").then((m) => m.KootaBreakdown),
  { ssr: false, loading: () => null }
);
const MangalDoshaStatus = dynamic(
  () => import("@/components/matching/MangalDoshaStatus").then((m) => m.MangalDoshaStatus),
  { ssr: false, loading: () => null }
);
const DetailedMatchingReport = dynamic(
  () =>
    import("@/components/matching/DetailedMatchingReport").then((m) => m.DetailedMatchingReport),
  { ssr: false, loading: () => null }
);
const MatchingCrossSell = dynamic(
  () => import("@/components/matching/MatchingCrossSell").then((m) => m.MatchingCrossSell),
  { ssr: false, loading: () => null }
);
const PaidReportCTA = dynamic(() => import("@/components/PaidReportCTA"), {
  ssr: false,
  loading: () => null,
});

export type MatchingSurface = "tool" | "share";

export interface MatchingResultsPanelProps {
  result: MatchingResponse;
  personAData: PersonInput | null;
  personBData: PersonInput | null;
  defaultLanguage: "en" | "hi";
  detailedResult: DetailedMatchingResponse | null;
  detailedLoading: boolean;
  detailedError: string | null;
  pdfLoading: boolean;
  onGetDetailed: () => void;
  onDownloadPdf: () => void;
  /** Inline tool flow resets the wizard; the /match share surface omits this
   * and gets a "Match Another Pair" link instead. */
  onReset?: () => void;
  /** Which surface renders the panel — sets offer analytics attribution. */
  surface: MatchingSurface;
}

export function MatchingResultsPanel({
  result,
  personAData,
  personBData,
  defaultLanguage,
  detailedResult,
  detailedLoading,
  detailedError,
  pdfLoading,
  onGetDetailed,
  onDownloadPdf,
  onReset,
  surface,
}: MatchingResultsPanelProps) {
  const hi = defaultLanguage === "hi";
  return (
    <div className="space-y-6">
      <MatchingScoreCard result={result} />
      <KootaBreakdown result={result} />
      <MangalDoshaStatus result={result} />
      <MarriageReportOffer
        personAData={personAData}
        personBData={personBData}
        defaultLanguage={defaultLanguage}
        surface={surface}
      />
      <MatchingCrossSell result={result} />
      <Recommendation recommendation={result.recommendation} />
      {result.match_id ? (
        <>
          <SavedLinkCard
            path={`/match/${result.match_id}${hi ? "?lang=hi" : ""}`}
            heading={hi ? "Yeh milan result save ho gaya hai" : "This match is saved"}
            body={
              hi
                ? "Link family ke saath share karein ya kabhi bhi dobara kholein — dono kundali details dobara bharne ki zaroorat nahi."
                : "Share the link with your family or return to it anytime — no need to re-enter both birth details."
            }
            shareLabel={hi ? "Link share karein" : "Share link"}
            copiedLabel={hi ? "Link copy ho gaya" : "Link copied"}
            analyticsEvent="match_share_clicked"
            analyticsProps={{ match_id: result.match_id, language: defaultLanguage, surface }}
          />
          <ValueMomentContactCapture
            target={{ type: "match", id: result.match_id }}
            language={defaultLanguage}
            placement={`kundli-matching-${surface}-value-moment`}
          />
        </>
      ) : null}
      {!detailedResult && (
        <DeeperInsightsCTA
          detailedLoading={detailedLoading}
          detailedError={detailedError}
          onGetDetailed={onGetDetailed}
        />
      )}
      {detailedResult && (
        <DetailedMatchingReport
          result={result}
          detailedResult={detailedResult}
          pdfLoading={pdfLoading}
          onDownloadPdf={onDownloadPdf}
        />
      )}
      <ResultActions onReset={onReset} />
    </div>
  );
}

function offerAnalytics(defaultLanguage: "en" | "hi", surface: MatchingSurface) {
  if (surface === "share") {
    return {
      source: "match-share",
      sourcePath: "/match",
      placement: "match_share_results_primary",
    };
  }
  return defaultLanguage === "hi"
    ? {
        source: "hi-kundli-matching",
        sourcePath: "/hi/kundli-matching",
        placement: "kundli_matching_results_primary",
      }
    : {
        source: "kundli-matching",
        sourcePath: "/kundli-matching",
        placement: "kundli_matching_results_primary",
      };
}

function MarriageReportOffer({
  personAData,
  personBData,
  defaultLanguage,
  surface,
}: {
  personAData: PersonInput | null;
  personBData: PersonInput | null;
  defaultLanguage: "en" | "hi";
  surface: MatchingSurface;
}) {
  const planCopy = reportOfferCopyFor("marriage_report", defaultLanguage);
  const analytics = offerAnalytics(defaultLanguage, surface);
  // Fire the offer-seen impression ONCE per presentation so this value surface's
  // reach is measurable. Before this the marriage offer rendered but emitted no
  // impression, so it read 0% reach in the funnel + the reach SLI (rule 46) — the
  // offer was invisible to monetization analytics. Hook runs before the early
  // return (rules of hooks) with a null key when birth data is missing.
  const ready = Boolean(personAData && personBData);
  useImpressionOnce(ready ? `marriage_report|${surface}|${analytics.sourcePath}` : null, () => {
    trackAgentRevenueOfferShown({
      offer_type: "report",
      plan_id: "marriage_report",
      source_path: analytics.sourcePath,
      intent: "chart_report",
      placement: analytics.placement,
      checkout_channel: "ishvaram_report",
      commerce_system: "ishvaram",
      customer_problem: "marriage",
      urgency: "high",
      route_rank_score: 96,
      route_rank_reason: "kundli_matching_result_marriage_report",
      ranked_offer_order: ["marriage_report"],
    });
  });
  // Narrow the nullables directly (not via `ready`) so TS knows they're non-null
  // for matchingReportPerson below — equivalent to `!ready`.
  if (!personAData || !personBData) return null;
  return (
    <PaidReportCTA
      planId="marriage_report"
      priceInr={planCopy.priceInr}
      headline={
        defaultLanguage === "hi"
          ? `₹${planCopy.priceInr} Kundali Milan Report paayein`
          : `Get the ₹${planCopy.priceInr} Marriage Matching Report`
      }
      subhead={
        defaultLanguage === "hi"
          ? "Family-shareable PDF: full Gun Milan, Mangal Dosh, D9/Navamsha, 5-year dasha timing, risks, strengths aur upay."
          : "Family-shareable PDF with full Gun Milan, Mangal Dosha, D9/Navamsha compatibility, 5-year dasha timing, risks, strengths, and practical remedies."
      }
      language={defaultLanguage}
      source={analytics.source}
      analyticsContext={{
        placement: analytics.placement,
        checkoutChannel: "ishvaram_report",
        commerceSystem: "ishvaram",
        intent: "chart_report",
        sourcePath: analytics.sourcePath,
        customerProblem: "marriage",
        urgency: "high",
        confidence: 0.92,
        routeRankScore: 96,
        routeRankReason: "kundli_matching_result_marriage_report",
        rankedOfferOrder: ["marriage_report"],
      }}
      reportInputs={{
        person_a: matchingReportPerson(personAData),
        person_b: matchingReportPerson(personBData),
        language: defaultLanguage,
      }}
    />
  );
}

function matchingReportPerson(person: PersonInput) {
  return {
    name: person.name,
    birth_date: person.birth_date,
    birth_time: person.birth_time || null,
    birth_place: person.birth_place,
  };
}

function Recommendation({ recommendation }: { recommendation?: string }) {
  return (
    <div className="card-sacred p-6 sm:p-8 bg-ish-accent-light">
      <h2 className="text-[16px] font-bold mb-3 text-ish-accent">Recommendation</h2>
      <p className="text-[14px] leading-relaxed text-ish-text-body">{recommendation}</p>
    </div>
  );
}

function ResultActions({ onReset }: { onReset?: () => void }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link
        href="/free-kundali"
        className="btn-sacred text-center font-semibold py-3 px-6 rounded-lg text-[14px]"
      >
        Get Full Kundali Reading &rarr;
      </Link>
      {onReset ? (
        <button
          onClick={onReset}
          className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200 text-ish-text-muted border border-ish-border"
        >
          Match Another Pair
        </button>
      ) : (
        <Link
          href="/kundli-matching"
          className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200 text-ish-text-muted border border-ish-border"
        >
          Match Another Pair
        </Link>
      )}
    </div>
  );
}
